import { inject } from "@angular/core";
import { CanActivateFn, ParamMap, Router, createUrlTreeFromSnapshot } from "@angular/router";

import { Unassigned } from "@bitwarden/common/admin-console/models/collections";
import { FeatureFlag } from "@bitwarden/common/enums/feature-flag.enum";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { CipherType } from "@bitwarden/common/vault/enums";
import { isGuid } from "@bitwarden/guid";

import {
  VAULT_FILTER_KEYS,
  VAULT_FILTER_NAMESPACE,
  VAULT_RESERVED_FILTER_KEYS,
} from "../components/vault-items-table/vault-items-table.component";
import { All } from "../models/routed-vault-filter.model";
import {
  folderCommands,
  VaultScope,
  VaultScopeType,
  vaultScopeCommands,
} from "../models/vault-scope";
import { VAULT_ITEM_FILTER, VaultItemFilter } from "../tokens/vault-item-filter.token";
import { NO_FOLDER, MY_VAULT } from "../utils/vault-filter-predicates";

/** Maps the legacy `?type=` string values to their numeric CipherType equivalents. */
const LEGACY_TYPE_MAP: Record<string, CipherType> = {
  login: CipherType.Login,
  card: CipherType.Card,
  identity: CipherType.Identity,
  note: CipherType.SecureNote,
  sshKey: CipherType.SshKey,
  driversLicense: CipherType.DriversLicense,
  bankAccount: CipherType.BankAccount,
  passport: CipherType.Passport,
};

/**
 * The legacy `?type=` values that are now side-nav scopes rather than table filters. These
 * redirect to their own route instead of a namespaced query param — see {@link VaultScope}.
 */
const LEGACY_SCOPE_MAP: Record<string, VaultScope> = {
  trash: { type: VaultScopeType.Trash },
  archive: { type: VaultScopeType.Archive },
};

/** All legacy param key names stripped from the URL during redirect. */
const LEGACY_KEYS = new Set([
  "type",
  "folderId",
  "sharedFolderId",
  "collectionId",
  "vaultId",
  "organizationId",
  "search",
]);

/**
 * The contributed filter's key (`VAULT_ITEM_FILTER`), which a legacy URL carries un-namespaced.
 * `undefined` with no provider, or when the key is a built-in or a legacy one.
 */
function contributedLegacyKey(): string | undefined {
  const filter: VaultItemFilter | null = inject(VAULT_ITEM_FILTER, { optional: true });
  const key = filter?.key;
  return key == null || VAULT_RESERVED_FILTER_KEYS.has(key) || LEGACY_KEYS.has(key)
    ? undefined
    : key;
}

/**
 * Extracts the legacy filter params from a query-param map, plus the contributed filter's param
 * when `contributedKey` is set. Returns null for each dimension that isn't present.
 */
function extractLegacyParams(params: ParamMap, contributedKey: string | undefined) {
  return {
    type: params.get("type"),
    folderId: params.get("folderId"),
    sharedFolderId: params.get("sharedFolderId") ?? params.get("collectionId"),
    organizationId: params.get("vaultId") ?? params.get("organizationId"),
    search: params.get("search"),
    contributed: contributedKey == null ? null : params.get(contributedKey),
  };
}

/**
 * Builds the query-param patch to apply during the redirect.
 * Legacy keys are mapped to their `vault.*` namespaced equivalents.
 */
function buildRedirectPatch(
  legacy: ReturnType<typeof extractLegacyParams>,
  contributedKey: string | undefined,
): Record<string, string> {
  const ns = VAULT_FILTER_NAMESPACE;
  const keys = VAULT_FILTER_KEYS;
  const patch: Record<string, string> = {};

  if (legacy.type === "favorites") {
    patch[`${ns}.${keys.favorites}`] = "true";
  } else if (legacy.type != null && LEGACY_TYPE_MAP[legacy.type] != null) {
    patch[`${ns}.${keys.type}`] = String(LEGACY_TYPE_MAP[legacy.type]);
  }

  if (legacy.folderId != null) {
    patch[`${ns}.${keys.folder}`] = legacy.folderId === Unassigned ? NO_FOLDER : legacy.folderId;
  }
  // `all` means "no shared-folder filter" in the new table — omit rather than map it.
  if (legacy.sharedFolderId != null && legacy.sharedFolderId !== All) {
    patch[`${ns}.${keys.sharedFolder}`] = legacy.sharedFolderId;
  }
  if (legacy.organizationId != null) {
    patch[`${ns}.${keys.vault}`] =
      legacy.organizationId === Unassigned ? MY_VAULT : legacy.organizationId;
  }
  if (legacy.search != null) {
    patch[`${ns}.${keys.search}`] = legacy.search;
  }
  if (contributedKey != null && legacy.contributed != null) {
    patch[`${ns}.${contributedKey}`] = legacy.contributed;
  }

  return patch;
}

/**
 * The folder id a URL names as its only filter, or `undefined` when it names none or filters by
 * anything else too — another legacy param, or a param already under the vault's namespace. Params
 * that are not filters, like `cipherId` and `action`, do not count.
 */
function soleFolderId(
  legacy: ReturnType<typeof extractLegacyParams>,
  params: ParamMap,
): string | undefined {
  const { folderId, ...others } = legacy;
  const filteredByOthers =
    Object.values(others).some((value) => value != null) ||
    params.keys.some((key) => key.startsWith(`${VAULT_FILTER_NAMESPACE}.`));

  return folderId != null && isGuid(folderId) && !filteredByOthers ? folderId : undefined;
}

/**
 * Redirects legacy vault URL params (`?type=`, `?folderId=`, etc.) to their
 * `queryParam="vault"` namespaced equivalents (`?vault.type=`, `?vault.folder=`, etc.)
 * when the VFO1Foundation feature flag is enabled. Non-legacy params (e.g. `cipherId`,
 * `action`) are preserved in the redirect.
 *
 * `?type=trash` and `?type=archive` are the exceptions: those are side-nav scopes now, so they
 * redirect to the scope's own route rather than to a filter chip — see {@link LEGACY_SCOPE_MAP}.
 * A `?folderId=` that is a guid and the only filter redirects to that folder's own route,
 * `/folders/:folderId`; combined with other filters it stays a chip.
 */
export const vaultFilterLegacyRedirectGuard: CanActivateFn = async (route) => {
  const configService = inject(ConfigService);
  const router = inject(Router);

  const contributedKey = contributedLegacyKey();

  const legacy = extractLegacyParams(route.queryParamMap, contributedKey);
  const hasLegacyParams = Object.values(legacy).some((v) => v != null);

  if (!hasLegacyParams) {
    return true;
  }

  const patch = buildRedirectPatch(legacy, contributedKey);
  const scope = legacy.type == null ? undefined : LEGACY_SCOPE_MAP[legacy.type];

  // A folder id alone names a folder route rather than a filter chip, so it leaves the patch. With
  // any other filter alongside — a bookmark of All items narrowed by folder and a type, say — the
  // page is still the vault, so the folder stays a chip. So does anything that is not a guid, and
  // the "unassigned" sentinel, since neither names a folder.
  const folderId = soleFolderId(legacy, route.queryParamMap);
  if (folderId != null) {
    delete patch[`${VAULT_FILTER_NAMESPACE}.${VAULT_FILTER_KEYS.folder}`];
  }

  // No mapped params, scope, or folder — nothing to redirect (e.g. ?type=all).
  if (Object.keys(patch).length === 0 && scope == null && folderId == null) {
    return true;
  }

  const vfo1Enabled = await configService.getFeatureFlag(FeatureFlag.VFO1Foundation);

  // Only applicable when VFO1Foundation is enabled
  if (!vfo1Enabled) {
    return true;
  }

  // Copy non-legacy params first, then apply the converted patch.
  // This preserves params like cipherId and action that the vault uses independently.
  //
  // If `type` was present but neither translated nor a scope (e.g. `all`, or a value from a
  // client this guard has not caught up with), keep it in the redirect so the legacy filter$ can
  // still apply it. Without this, a URL like ?vaultId=<org>&type=all would silently drop the type
  // filter when the redirect fires for the org param.
  const typeTranslated =
    patch[`${VAULT_FILTER_NAMESPACE}.${VAULT_FILTER_KEYS.type}`] != null ||
    patch[`${VAULT_FILTER_NAMESPACE}.${VAULT_FILTER_KEYS.favorites}`] != null;
  const keysToStrip = new Set(LEGACY_KEYS);
  if (contributedKey != null) {
    keysToStrip.add(contributedKey);
  }
  if (legacy.type != null && !typeTranslated && scope == null) {
    keysToStrip.delete("type");
  }

  const queryParams: Record<string, string | string[]> = {};
  for (const key of route.queryParamMap.keys) {
    if (!keysToStrip.has(key)) {
      const values = route.queryParamMap.getAll(key);
      queryParams[key] = values.length > 1 ? values : values[0];
    }
  }
  Object.assign(queryParams, patch);

  // A scope is a different route, not a param on this one, so it can't ride along on the
  // snapshot-relative tree the param-only redirect uses.
  if (scope != null) {
    return router.createUrlTree(vaultScopeCommands(scope), { queryParams });
  }

  if (folderId != null) {
    return router.createUrlTree(folderCommands(folderId), { queryParams });
  }

  return createUrlTreeFromSnapshot(route, [], queryParams);
};
